import styled from '@emotion/styled';
import { Accordion, AccordionSummary, Box, Theme } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import brokenHeart from 'assets/brokenHeart.svg';
import checkIcon from 'assets/checkIcon.svg';
import contactUsPaperPlane from 'assets/contactUsPaperPlane.svg';
import expandIcon from 'assets/expandIcon.svg';
import facebookMessengerFilledBlue from 'assets/facebookMessengerFilledBlue.svg';
import handsShake from 'assets/handsShake.svg';
import ltdPlan from 'assets/ltdPlan.svg';
import murshePlan from 'assets/murshePlan.svg';
import paturPlan from 'assets/paturPlan.svg';
import whatsappFilledBlue from 'assets/whatsappFilledBlue.svg';
import { breakpoints, getMQ } from 'src/helpers';
import colors from 'src/styles/colors';

export const InfoPagesContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Paragraph = styled.div`
  margin-top: 35px;
  font-weight: 400;
  color: #162154;
  text-align: center;
  white-space: pre-line;
  font-size: 17px;

  ${getMQ(breakpoints.l)} {
    font-size: 20px;
  }
`;

export const InfoPagesHeaderTitle = styled.h1`
  font-weight: 700;
  font-size: 48px;
  line-height: 70px;
  color: ${colors.darkBlue};
  text-align: center;
`;

export const InfoPagesHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 99px 45px 20px 45px;
  z-index: 2;
  position: relative;
`;

export const QuestionsPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 30px;

  ${getMQ(breakpoints.l)} {
    padding: 0px 90px;
  }

  ${getMQ(breakpoints.xl)} {
    padding: 0px 150px;
  }
`;

export const AccordionTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.darkBlue};
  margin: 10px 0px 10px 25px;

  ${getMQ(breakpoints.l)} {
    font-size: 24px;
  }
`;

export const AccordionParagraph = styled.div`
  margin: 20px 0;
  font-weight: 500;
  color: #8a90a9;
  white-space: pre-line;
  font-size: 16px;

  ${getMQ(breakpoints.l)} {
    font-size: 20px;
  }
`;

export const StyledAccordionSummary = styled(AccordionSummary)(({ theme }: { theme?: Theme }) => ({
  padding: '0px',
  margin: '13px',
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: colors.primary,
  },
}));

export const ExpandIconStyled = styled(expandIcon)`
  fontsize: '35px';
`;

export const StyledAccordion = muiStyled(Accordion)(({ theme }: { theme?: Theme }) => ({
  border: '1px solid #DCDEE5',
  borderRadius: '8px',
  background: 'white',
  marginBottom: '15px',
  alignContent: 'center',
  padding: '0px',

  ...(theme?.breakpoints.up('lg') && {
    padding: '0px 20px',
  }),

  '&:before': {
    display: 'none',
  },
}));

export const ContactUsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  text-align: center;
  padding: 80px 45px;
  gap: 20px;

  ${getMQ(breakpoints.l)} {
    flex-direction: row;
  }
`;

export const ContactUsTitle = styled.h1`
  font-weight: 700;
  color: ${colors.primary};
  margin: 0 15px;
`;

export const ContactUsButton = styled.button`
  padding: 11px 43px;
  background: ${colors.primary};
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
  border-radius: 8px;
  border: 0;
  color: ${colors.white};
  font-weight: bold;
  font-size: 16px;
  display: flex;
  width: 180px;
  height: 40px;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  margin: auto;
`;

export const FormTitle = muiStyled('h1')(({ theme }: { theme?: Theme }) => ({
  fontWeight: 700,
  fontSize: '24px',
  lineHeight: '35px',
  color: colors.darkBlue,
  textAlign: 'start',
  whiteSpace: 'pre-line',
}));

export const SocialButtonsTitle = muiStyled(FormTitle)(({ theme }: { theme: Theme }) => ({
  marginTop: '100px',

  ...(theme.breakpoints.up('md') && {
    marginTop: '0px',
  }),
}));

export const SocialButtons = styled.div`
  display: flex;
  flex-direction: row;

  ${getMQ(breakpoints.l)} {
    margin-top: 50px;
  }
`;

export const Divider = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'none',

  ...(theme.breakpoints.up('md') && {
    display: 'flex',
    width: '1px',
    background: '#e7e7e7',
    margin: 'auto',
    height: '456px',
  }),
}));

export const ContactUsPaperPlaneStyled = muiStyled(contactUsPaperPlane)(({ theme }: { theme: Theme }) => ({
  width: '200px',
  height: '150px',
  float: 'left',
  marginTop: '0px',

  ...(theme.breakpoints.up('md') && {
    marginTop: '230px',
    width: '283px',
    height: '150px',
  }),

  ...(theme.breakpoints.up('lg') && {
    width: '383px',
    height: '250px',
  }),
}));

export const WhatsappFilledBlueStyled = muiStyled(whatsappFilledBlue)(({ theme }: { theme?: Theme }) => ({
  marginLeft: '16.5px',
  display: 'flex',
  '&:hover': {
    opacity: 0.5,
  },
}));

export const FacebookMessengerFilledBlueStyled = muiStyled(facebookMessengerFilledBlue)(
  ({ theme }: { theme?: Theme }) => ({
    marginLeft: '16.5px',
    display: 'flex',
    '&:hover': {
      opacity: 0.5,
    },
  }),
);

export const AboutUsParagraphWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  margin: 50px 0px;
  padding: 0px 20px;
  align-items: flex-start;
  width: 100%;

  ${getMQ(breakpoints.l)} {
    padding: 0px 100px;
  }

  ${getMQ(breakpoints.xl)} {
    justify-content: space-around;
    flex-direction: row;
    padding: 0px;
  }

  ${getMQ(breakpoints.xxl)} {
    padding: 0px 200px;
  }
`;

export const AboutUsParagraphContainer = styled.div`
  position: relative;
  width: 100%;

  ${getMQ(breakpoints.xl)} {
    width: 650px;
  }

  ${getMQ(breakpoints.xxl)} {
    width: 800px;
  }
`;

export const AboutUsParagraphTitle = styled.div`
  font-weight: 700;
  font-size: 42px;
  line-height: 62px;
  color: ${colors.darkBlue};
  margin-bottom: 20px;

  ${getMQ(breakpoints.l)} {
    font-size: 52px;
  }
`;

export const AboutUsParagraphText = styled.div`
  font-size: 18px;
  line-height: 26px;
  color: #8a90a9;
  text-align: start;

  ${getMQ(breakpoints.l)} {
    line-height: 32px;
  }

  ${getMQ(breakpoints.xl)} {
    // width: 600px;
  }

  ${getMQ(breakpoints.xxl)} {
    // width: 710px;
  }
`;

type AboutUsParagraphImageProps = {
  svgImage?: boolean;
};

export const AboutUsParagraphImage = styled.div<AboutUsParagraphImageProps>`
  align-self: center;
  width: 280px;
  height: 280px;
  margin-bottom: 35px;

  img {
    width: 280px;
    height: 280px;
  }

  ${getMQ(breakpoints.m)} {
    width: 317px;
    height: 317px;

    img {
      width: 317px;
      height: 317px;
    }
  }

  ${getMQ(breakpoints.xl)} {
    margin-bottom: 0px;
    width: 380px;
    height: 380px;

    img {
      width: 380px;
      height: 380px;
    }
  }
`;

export const AdjustmentQuestionnairePageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 30px;
  width: 100%;

  ${getMQ(breakpoints.l)} {
    padding: 0px 165px;
  }

  ${getMQ(breakpoints.xl)} {
    padding: 0px 333px;
  }

  ${getMQ(breakpoints.xxl)} {
    padding: 0px 555px;
  }
`;

export const AdjustmentQuestionnaireContentContainer = styled.div`
  width: 100%;
  background: white;
  align-items: center;
  text-align: center;
`;

export const StyledQuestionContainer = styled.div`
  margin-bottom: 85px;
`;

export const StyledQuestionTitle = styled.h3`
  color: #162154;
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: 500;
  line-height: 40px;
  letter-spacing: 0px;
  text-align: center;
`;

export const MatchTitle = styled.div`
  color: #162154;
  margin-bottom: 16px;
  letter-spacing: 0px;
  text-align: center;
  font-size: 32px;
  font-weight: 700;
  line-height: 47px;
  letter-spacing: 0em;
`;

export const MatchSubTitle = styled.div`
  color: #162154;
  margin-bottom: 16px;
  letter-spacing: 0px;
  text-align: center;
  font-size: 24px;
  font-weight: 400;
  line-height: 35px;
  letter-spacing: 0em;
`;

export const MatchIcon = styled.div`
  text-align: center;
  margin-bottom: 15px;
`;

export const MatchButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

export const HandsShakeStyled = styled(handsShake)`
  width: 175px;
  height: 175px;
`;

export const BrokenHeartStyled = styled(brokenHeart)`
  width: 175px;
  height: 175px;
`;

export const JooniFeaturesContainer = styled.div`
  padding: 0px 20px;
  display: flex;
  text-align: center;
  width: 100%;
  align-self: center;
  margin-bottom: 111px;

  ${getMQ(breakpoints.m)} {
    padding: 0px 90px;
  }

  ${getMQ(breakpoints.l)} {
    padding: 0px 50px;
  }

  ${getMQ(breakpoints.xl)} {
    width: 1800px;
    padding: 0px 220px;
  }
`;

export const JooniFeature = styled.div`
  flex-direction: column;
  display: flex;
  align-items: center;
  text-align-last: center;
  border: 2px solid #cedfff;
  box-shadow: 0px 0px 30px rgba(217, 246, 255, 0.25);
  border-radius: 16px;
  margin: 0px 0px 56px 0px;
  width: 100%;
  height: auto;
  padding: 20px;
  background: #f8f9ff;

  ${getMQ(breakpoints.l)} {
    max-width: 620px;
    flex-direction: row;
    height: 150px;
    text-align-last: start;
  }
`;

export const JooniFeatureImage = styled.div`
  img {
    margin: 0px;
    margin-bottom: -20px;
    max-width: 125px;
    min-width: 125px;
  }
`;

export const JooniFeatureContent = styled.div`
  flex-direction: column;
  display: flex;
  text-align: center;
  font-size: 14px;
  color: ${colors.black};

  ${getMQ(breakpoints.m)} {
    font-size: 16px;
  }

  ${getMQ(breakpoints.l)} {
    text-align: right;
  }
`;

export const JooniFeatureItemTitle = styled.div`
  font-weight: bold;
  font-size: 20px;
  margin-bottom: 5px;
`;

export const JooniFeatureItemText = styled.div`
  color: #8a90a9;
`;

export const JooniFeaturesSubTitle = styled.div`
  font-size: 20px;
  line-height: 26px;
  color: ${colors.darkBlue};
  text-align: center;
  margin: -25px 0px 50px 0px;
  padding: 0px 70px;

  ${getMQ(breakpoints.l)} {
    font-size: 26px;
  }
`;

export const PageNotFoundImage = styled.div`
  width: 355px;
  background: white;
  margin-top: -190px;
  margin-bottom: 50px;
  align-self: center;
  padding-top: 60px;

  ${getMQ(breakpoints.m)} {
    margin-top: -190px;
    width: 380px;
  }

  ${getMQ(breakpoints.l)} {
    margin-top: -145px;
    width: 555px;
  }
`;

export const PageNotFoundFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
  align-self: center;
  font-weight: 400;
  font-size: 28px;
  line-height: 41px;
  text-align: right;
  color: ${colors.darkBlue};
  margin-bottom: 60px;
`;

export const PageNotFoundFooterTitle = styled.div`
  font-weight: 700;
  font-size: 28px;

  ${getMQ(breakpoints.l)} {
    font-size: 48px;
  }
`;

export const PageNotFoundFooterText = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 14px;
  margin-top: -20px;

  ${getMQ(breakpoints.l)} {
    margin-top: 0px;
    font-size: 28px;
  }
`;

export const PricacyAndTermsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: -40px;
`;

export const PricacyAndTerms = styled.div`
  border-radius: 10px;
  background: ${colors.lightBackground};
  margin: 4%;
  padding: 20px;
  text-align: start;

  ${getMQ(breakpoints.xl)} {
    padding: 40px 80px;
    margin: 4% 14%;
  }
`;

export const InfoVideoButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  white-space: nowrap;
  width: 88vw;
  overflow-x: scroll;
  overflow-y: hidden;
  min-height: 155px;

  ${getMQ(breakpoints.m)} {
    min-height: 170px;
    overflow-x: auto;
    overflow-y: hidden;
  }

  ${getMQ(breakpoints.l)} {
    justify-content: center;
    margin-bottom: 0px;
    overflow-x: auto;
    overflow-y: hidden;
    width: 100%;
  }
`;

export const InfoVideosContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  height: 350px;

  ${getMQ(breakpoints.m)} {
    margin-top: 100px;
    margin-bottom: 100px;
  }

  ${getMQ(breakpoints.l)} {
    height: 750px;
    margin-top: 0px;
    margin-bottom: 0px;
  }
`;

type InfoVideoButtonProps = {
  selected: boolean;
};

export const InfoVideoButton = styled.button<InfoVideoButtonProps>`
  background-color: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  opacity: ${(props) => (props.selected ? 1 : 0.7)};
  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover {
    opacity: 1;
  }
`;

export const InfoVideoImageContainer = styled.div`
  width: 120px;
  height: 65px;
  margin: 0px 10px 10px 10px;

  ${getMQ(breakpoints.m)} {
    width: 169px;
    height: 95px;
  }
`;

export const InfoVideoButtonTitle = styled.div`
  font-size: 14px;
  max-width: 111px;
  white-space: pre-wrap;

  ${getMQ(breakpoints.l)} {
    font-size: 15px;
  }
`;

export const PlanItemTitle = styled.div`
  color: #4357f2;
  font-weight: bold;
  font-size: 24px;
`;

export const PlanItemPriceWrapper = styled(Box)(({ theme }: { theme?: Theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  color: theme?.palette.text.secondary,
}));

export const PlanItemPrice = styled.div`
  font-size: 28px;
  font-weight: bold;
`;

export const PlanItemCurrency = styled.div`
  font-size: 20px;
  font-weight: bold;
  margin-inline-end: 2px;
`;

export const PlanItemPeriod = styled.div`
  font-size: 20px;
  margin-inline-end: 5px;
`;

export const PlanItemListDetail = styled.div`
  margin-top: 18px;
  font-size: 20px;
  color: ${colors.lightBlueText};
  height: 32px;
`;

export const PlansContentContainer = styled.div`
  align-self: center;
  border: 1px solid #d9e6fa;
  border-radius: 10px;
  box-shadow: 0px 1px 20px rgba(222, 222, 222, 0.25);
  margin: 20px 12px;
  display: flex;
  min-width: 375px;
  height: 950px;
  justify-content: space-between;
  flex-direction: column;
  background: #fff;
  position: relative;
  overflow: hidden;

  ${getMQ(breakpoints.m)} {
    width: 406px;
  }
`;

export const PlanItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  flex-direction: column;

  ${getMQ(breakpoints.m)} {
    flex-direction: row;
  }
`;

export const PlanItemContainer = styled(Box)(({ theme }: { theme?: Theme }) => ({
  padding: theme?.spacing(0, 2, 2, 2),
  margin: theme?.spacing(3, 0, 3.5, 0),
  width: '100%',
}));

export const PlanItemHeader = styled.div`
  justify-content: center;
  flex-direction: column;
  display: flex;
  margin-right: 26px;

  ${getMQ(breakpoints.l)} {
    margin-right: 65px;
  }
`;

export const PlanItemList = styled.div`
  margin-top: 38px;
  margin-right: 29px;

  ${getMQ(breakpoints.l)} {
    margin-right: 69px;
  }
`;

export const PlanItemCheckIcon = styled(checkIcon)`
  margin: 0 15px;
`;

export const PlansButtonContainer = styled.div`
  padding-bottom: 0;
  display: flex;
  justify-content: center;
  padding-bottom: 20px;
`;

export const PlansPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 24px 56px 24px;
  width: 100%;
  justify-content: center;
  position: relative;
  z-index: 2;

  ${getMQ(breakpoints.m)} {
    padding: 6px 39px 56px 39px;
  }

  ${getMQ(breakpoints.l)} {
    flex-direction: row;
  }
`;

export const PaturPlanStyled = styled(paturPlan)`
  width: 184px;
  height: 184px;
  align-self: center;
  margin-bottom: 45px;
  margin-top: 20px;
  margin-right: -26px;

  ${getMQ(breakpoints.l)} {
    margin-right: -65px;
  }
`;

export const MurshePlanStyled = styled(murshePlan)`
  width: 184px;
  height: 184px;
  align-self: center;
  margin-bottom: 45px;
  margin-top: 20px;
  margin-right: -26px;

  ${getMQ(breakpoints.l)} {
    margin-right: -65px;
  }
`;

export const LtdPlan = styled(ltdPlan)`
  width: 184px;
  height: 184px;
  align-self: center;
  margin-bottom: 45px;
  margin-top: 20px;
  margin-right: -26px;

  ${getMQ(breakpoints.l)} {
    margin-right: -65px;
  }
`;

export const Soon = styled.div`
  background: ${colors.darkBlue};
  color: white;
  text-align: center;
  margin-top: 33px;
  width: 200px;
  height: 30px;
  font-weight: 700;
  align-self: center;
  transform: rotate(41.55deg);
  position: absolute;
  right: -40px;
`;
